import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, X, AlertCircle, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { StaticPageFormData } from '../../types/staticPage.types';
import { toast } from 'react-toastify';
import { staticPagesService } from '../../services/api/staticPagesService';

interface Section {
  heading: string;
  description: string;
  tableTitle: string;
  columns: string[];
  rows: string[][];
}

const emptySection = (): Section => ({
  heading: '',
  description: '',
  tableTitle: '',
  columns: [''],
  rows: [['']],
});

function esc(t: string) {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function toHtml(sections: Section[]): string {
  return sections.map((s) => {
    let h = '';
    if (s.heading) h += `<h5 class="font-weight-bold">${esc(s.heading)}</h5>\n`;
    if (s.description) h += `<h6 class="incentive-main-title sub-heading mt-0 mt-md-2 mb-3 font-weight-normal pt-2">${esc(s.description)}</h6>\n`;
    if (s.tableTitle) h += `<h5 style="text-align:center;"><b>${esc(s.tableTitle)}</b></h5>\n`;
    if (s.columns.length && s.rows.length) {
      h += `<div class="table-responsive incentive-ess-policy text-center">\n<table class="table table-bordered text-center">\n<thead>\n<tr>\n`;
      s.columns.forEach((c) => (h += `<th>${esc(c)}</th>\n`));
      h += `</tr>\n</thead>\n<tbody>\n`;
      s.rows.forEach((r) => {
        h += `<tr>\n`;
        s.columns.forEach((_, i) => (h += `<td style="text-align:center !important;">${esc(r[i] || '')}</td>\n`));
        h += `</tr>\n`;
      });
      h += `</tbody>\n</table>\n</div>\n`;
    }
    return h;
  }).join('\n');
}

const AssetsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [title, setTitle] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [sections, setSections] = useState<Section[]>([emptySection()]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditMode && id) fetchPage(parseInt(id));
  }, [id, isEditMode]);

  const fetchPage = async (pageId: number) => {
    try {
      setFetchingData(true);
      const res = await staticPagesService.getById(pageId);
      if (res.success) {
        const d = res.data;
        setTitle(d.title || '');
        setSectionName(d.section || '');
        setDisplayOrder(d.display_order || 0);
        setIsActive(d.is_active);
        if (d.metadata?.sections) {
          setSections(d.metadata.sections);
        }
      }
    } catch {
      toast.error('Failed to load data');
      navigate('/assets-management');
    } finally {
      setFetchingData(false);
    }
  };

  const updateSection = useCallback((i: number, field: keyof Section, value: any) => {
    setSections((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], [field]: value };
      return u;
    });
  }, []);

  const addSection = () => setSections((p) => [...p, emptySection()]);
  const removeSection = (i: number) => { if (sections.length > 1) setSections((p) => p.filter((_, j) => j !== i)); };

  const addColumn = (si: number) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      s.columns = [...s.columns, ''];
      s.rows = s.rows.map((r) => [...r, '']);
      u[si] = s; return u;
    });
  };

  const removeColumn = (si: number, ci: number) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      if (s.columns.length <= 1) return prev;
      s.columns = s.columns.filter((_, i) => i !== ci);
      s.rows = s.rows.map((r) => r.filter((_, i) => i !== ci));
      u[si] = s; return u;
    });
  };

  const updateColumn = (si: number, ci: number, val: string) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      s.columns = [...s.columns]; s.columns[ci] = val;
      u[si] = s; return u;
    });
  };

  const addRow = (si: number) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      s.rows = [...s.rows, new Array(s.columns.length).fill('')];
      u[si] = s; return u;
    });
  };

  const removeRow = (si: number, ri: number) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      if (s.rows.length <= 1) return prev;
      s.rows = s.rows.filter((_, i) => i !== ri);
      u[si] = s; return u;
    });
  };

  const updateCell = (si: number, ri: number, ci: number, val: string) => {
    setSections((prev) => {
      const u = [...prev]; const s = { ...u[si] };
      s.rows = s.rows.map((r, rj) => rj === ri ? r.map((c, cj) => cj === ci ? val : c) : r);
      u[si] = s; return u;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (!sections.some((s) => s.heading.trim() || s.description.trim() || s.tableTitle.trim()))
      errs.sections = 'At least one section needs content';
    if (displayOrder < 0) errs.display_order = 'Must be 0 or greater';
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error('Please fix errors'); return; }

    try {
      setLoading(true);
      const payload: StaticPageFormData = {
        slug: 'assets',
        section: sectionName,
        title,
        content: toHtml(sections),
        metadata: { sections },
        display_order: displayOrder,
        is_active: isActive,
      };

      if (isEditMode && id) {
        await staticPagesService.update(parseInt(id), payload);
        toast.success('Updated successfully');
      } else {
        await staticPagesService.create(payload);
        toast.success('Created successfully');
      }
      navigate('/assets-management');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Asset Section' : 'Create Asset Section'}
        </h1>
        <Link to="/assets-management" className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <ArrowLeft size={18} className="mr-2" /> Back to List
        </Link>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title & Section Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                className={`block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
                placeholder="Enter title" />
              {errors.title && <p className="mt-1 text-sm text-red-600"><AlertCircle size={14} className="inline mr-1" />{errors.title}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Name</label>
              <input type="text" value={sectionName} onChange={(e) => setSectionName(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., road_mileage" />
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Content Sections</h2>
              <button type="button" onClick={addSection} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
                <Plus size={16} className="mr-1" /> Add Section
              </button>
            </div>

            {errors.sections && <p className="text-sm text-red-600"><AlertCircle size={14} className="inline mr-1" />{errors.sections}</p>}

            {sections.map((sec, si) => (
              <div key={si} className="border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">
                    Section {si + 1}{sec.heading ? `: ${sec.heading}` : ''}
                  </span>
                  {sections.length > 1 && (
                    <button type="button" onClick={() => removeSection(si)} className="text-xs text-red-600 hover:text-red-800">
                      <Trash2 size={14} className="inline mr-1" />Remove
                    </button>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
                    <input type="text" value={sec.heading} onChange={(e) => updateSection(si, 'heading', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., ROADS" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={sec.description} onChange={(e) => updateSection(si, 'description', e.target.value)} rows={2}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                      placeholder="Description..." />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Table Title</label>
                    <input type="text" value={sec.tableTitle} onChange={(e) => updateSection(si, 'tableTitle', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Road mileage Status..." />
                  </div>

                  {/* Columns */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Columns</label>
                      <button type="button" onClick={() => addColumn(si)} className="text-xs text-blue-600 hover:text-blue-800">
                        <Plus size={14} className="inline mr-1" />Add Column
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sec.columns.map((col, ci) => (
                        <div key={ci} className="flex items-center gap-1">
                          <input type="text" value={col} onChange={(e) => updateColumn(si, ci, e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-44"
                            placeholder={`Column ${ci + 1}`} />
                          {sec.columns.length > 1 && (
                            <button type="button" onClick={() => removeColumn(si, ci)} className="p-1 text-gray-400 hover:text-red-500">
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Table */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Table Data</label>
                      <button type="button" onClick={() => addRow(si)} className="text-xs text-blue-600 hover:text-blue-800">
                        <Plus size={14} className="inline mr-1" />Add Row
                      </button>
                    </div>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-2 text-xs text-gray-500 w-10">#</th>
                            {sec.columns.map((col, ci) => (
                              <th key={ci} className="px-2 py-2 text-xs font-medium text-gray-600 text-left">{col || `Col ${ci + 1}`}</th>
                            ))}
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {sec.rows.map((row, ri) => (
                            <tr key={ri}>
                              <td className="px-2 py-1 text-xs text-gray-400 text-center">{ri + 1}</td>
                              {sec.columns.map((_, ci) => (
                                <td key={ci} className="px-1 py-1">
                                  <input type="text" value={row[ci] || ''} onChange={(e) => updateCell(si, ri, ci, e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                </td>
                              ))}
                              <td className="px-1 py-1 text-center">
                                {sec.rows.length > 1 && (
                                  <button type="button" onClick={() => removeRow(si, ri)} className="p-0.5 text-gray-400 hover:text-red-500">
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={addSection}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50">
              <Plus size={16} className="inline mr-1 -mt-0.5" /> Add Another Section
            </button>
          </div>

          {/* Display Order & Active */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(Number(e.target.value))} min="0"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
              </label>
              <span className="ml-2 text-sm text-gray-500">(Visible on website)</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button type="button" onClick={() => navigate('/assets-management')} disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <X size={18} className="mr-2" /> Cancel
            </button>
            <button type="submit" disabled={loading}
              className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>{isEditMode ? 'Updating...' : 'Creating...'}</>
              ) : (
                <><Save size={18} className="mr-2" />{isEditMode ? 'Update' : 'Create'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetsForm;
